import { Avatar as MuiAvatar, Tooltip } from '@mui/material';
import { useIntl } from 'react-intl';
import { initialsOf, type Assignee } from '../model';

type AssigneeAvatarProps = {
    /** Whoever holds the patient; undefined renders the empty "Unassigned" slot. */
    assignee: Assignee | undefined;
};

/**
 * 18px assignee avatar with initials, square per the app-wide MuiAvatar
 * radius. A holder who is not a listed user, or not known yet, gets no
 * initials, so they never read as a person. Unassigned renders as an empty
 * dash-bordered slot (MuiAvatar's person-icon fallback is suppressed).
 */
export default function AssigneeAvatar({ assignee }: AssigneeAvatarProps) {
    const intl = useIntl();
    const name = assignee?.name;
    return (
        <Tooltip
            title={name ?? intl.formatMessage({ id: 'patientList.unassigned' })}
            placement="left"
        >
            <MuiAvatar
                sx={{
                    width: 18,
                    height: 18,
                    bgcolor: name ? 'background.raised' : 'transparent',
                    border: 1,
                    borderColor: 'divider',
                    borderStyle: name ? 'solid' : 'dashed',
                    fontFamily: 'fontFamilyMonospace',
                    fontSize: 9,
                    color: 'text.secondary',
                    '& .MuiAvatar-fallback': { display: 'none' },
                }}
            >
                {assignee?.listed ? initialsOf(assignee.name) : undefined}
            </MuiAvatar>
        </Tooltip>
    );
}
